import ApexImage from '../Components/Icons/Rapid Robots/Apex.jpg';
import DeltaImage from '../Components/Icons/Rapid Robots/Delta.jpg';
import FluxImage from '../Components/Icons/Rapid Robots/Flux.jpg';
import MachImage from '../Components/Icons/Rapid Robots/Mach.jpg';
import NovaImage from '../Components/Icons/Rapid Robots/Nova.jpg';
import PivotImage from '../Components/Icons/Rapid Robots/Pivot.jpg';

const viteDevelopmentFlag = import.meta.env.VITE_DEVELOPMENT;

export const DEVELOPMENT = viteDevelopmentFlag ? viteDevelopmentFlag.toLowerCase() === 'true' : import.meta.env.DEV;

/** BPS-641: Skid adapters are not available at launch; re-enable when skid mode ships. */
export const SKID_MODE_UI_ENABLED = false;

export const BPS_SKU = '760-0602-698';

// The amount of data in percent that we will allocate to the app on a users computer/browers before they need to clear data.
export const quotaStorageLimit = 90;

export const BPR_FIRMWARE_ENVIRONMENT = import.meta.env.VITE_BPR_FIRMWARE_ENVIRONMENT || 'staging';

export const BPR_FIRMWARE_BASE_S3_URL = 'https://blueprint-studio-firmware-deployment.s3.us-east-1.amazonaws.com/';

export const BPR_FIRMWARE_BASE_URL = BPR_FIRMWARE_BASE_S3_URL + BPR_FIRMWARE_ENVIRONMENT + '-fw';

/** Support article linked from wired and wireless robot connection dialogs. */
export const BLUEPRINT_STUDIO_CONNECTION_HELP_URL = 'https://help.sphero.com/sphero-support/connecting-in-blueprint-studio';

/**
 * List of possible robot types.
 */
export const ROBOTS = {
  BPRDU: {
    type: 'BPRDU',
    name: 'Swerve Drive Robot',
    alias: 'BPRDU',
    id: 22,
    prefix: 'DU-',
    slug: 'bpr-du',
  },
  UNSUPPORTED: {
    type: 'UNSUPPORTED',
    name: 'robotName',
    prefix: '',
    id: 0,
    alias: 'UNSUPPORTED',
  },
  NONE: {
    type: 'NONE',
    name: 'robotName',
    prefix: '',
    id: 0,
    alias: 'NONE',
  },
};

/**
 * used when an array of robots is better than an object
 */
export const RobotArray = Object.values(ROBOTS);

/**
 * Default selected robot for connection dialogs
 */
export const DEFAULT_SELECTED_ROBOT = ROBOTS.BPRDU;

/** Drive Unit Python program slot names as used by the firmware asset IDs (slot1–slot8). */
export const SLOT_NAMES = ['slot1', 'slot2', 'slot3', 'slot4', 'slot5', 'slot6', 'slot7', 'slot8'] as const;
export type SlotName = (typeof SLOT_NAMES)[number];

/** User-facing slot number (1–8) from a firmware slot asset id (`slot1`–`slot8`). */
export function slotNumberFromSlotName(slotName: string): number {
  return Number(slotName.slice(4));
}

/** Firmware slot asset id for a user-facing slot number (1–8). */
export function slotNameFromSlotNumber(slotNumber: number): SlotName {
  return `slot${slotNumber}` as SlotName;
}

// Non-port remote device system info occupies indices 0..8; port-backed electronics use 9..16.
export const REMOTE_DEVICE_SYSTEM_INFO_NON_PORT_INDEX_MAX = 8;
export const REMOTE_DEVICE_SYSTEM_INFO_PORT_INDEX_START = 9;
export const REMOTE_DEVICE_SYSTEM_INFO_INDEX_MAX = 16;

/**
 * List of possible robot error codes.
 */
export const RobotErrorCode = {
  DEFAULT: 30000,
  NO_ROBOT: 30001,
  CANCELLED: 30002,
  NO_BLUETOOTH: 30003,
  NOT_SUPPORTED: 30004,
  NO_INTERNET: 30005,
  UNSUPPORTED_FIRMWARE: 30006,
  NO_BLE: 30007,
  BLUETOOTH_DISABLED: 30008,
  V2_FIRMWARE_OUTDATED: 30009,
  FIRMWARE_VERSIONS_FAILED: 30010,
};

export const DefaultSettings = {
  color: { r: 0, g: 133, b: 202 },
  speed: 166,
  sensorData: false,
  joystick: false,
  previousRobot: ROBOTS.NONE,
};

export const DEFAULT_INTERVAL = 100;

export const MAXIMUM_RSSI = -20;
export const MINIMUM_RSSI = -100;

const CONNECTION_TYPES = {
  BLE: 'bluetooth',
  SERIAL: 'serial',
};

export const BATTERY_STATES = {
  LEVEL1: 'level1',
  LEVEL2: 'level2',
  LEVEL3: 'level3',
  LEVEL4: 'level4',
  LEVEL5: 'level5',
};

export const POWER_STATES = {
  CRITICAL: 'critical',
  LOW: 'low',
  OK: 'ok',
};

export const FUEL_GAUGE_STATES = {
  INITIALIZING: 'initializing',
  IN_CHARGER_NO_BATTERY: 'in_charger_no_battery',
  IN_CHARGER_FULLY_CHARGED: 'in_charger_fully_charged',
  IN_CHARGER_CHARGING: 'in_charger_charging',
  IN_CHARGER_DISCHARGING_CRITICAL: 'in_charger_discharging_critical',
  IN_CHARGER_DISCHARGING_LOW: 'in_charger_discharging_low',
  IN_CHARGER_DISCHARGING_OK: 'in_charger_discharging_ok',
  OUT_OF_CHARGER_NO_BATTERY: 'out_of_charger_no_battery',
  OUT_OF_CHARGER_CRITICAL: 'out_of_charger_critical',
  OUT_OF_CHARGER_LOW: 'out_of_charger_low',
  OUT_OF_CHARGER_OK: 'out_of_charger_ok',
  ERROR: 'error',

  ok: ['in_charger_fully_charged', 'in_charger_discharging_ok', 'out_of_charger_ok'],
  low: ['in_charger_discharging_low', 'out_of_charger_low'],
  critical: ['in_charger_discharging_critical', 'out_of_charger_critical'],

  charging: [
    'in_charger_no_battery',
    'in_charger_fully_charged',
    'in_charger_charging',
    'in_charger_discharging_ok',
    'in_charger_discharging_low',
    'in_charger_discharging_critical',
  ],
  notCharging: ['out_of_charger_no_battery', 'out_of_charger_critical', 'out_of_charger_low', 'out_of_charger_ok'],
};

// Below this level we consider a battery low if it is not critical
export const BATTERIES = {
  PERCENTAGE: [20, 40, 60, 80],
};

// Legacy battery types for backwards compatibility
export const BATTERY_TYPE = {
  [ROBOTS.BPRDU.type]: BATTERIES.PERCENTAGE,
};

export const IR_CHANNELS = [0, 1, 2, 3, 4, 5, 6, 7];

export const RobotActions = {
  STATE: {
    DISCONNECTED: 'disconnected',
    CONNECT: 'connect',
    CONNECTION_TYPE: CONNECTION_TYPES,
    RECONNECT: 'reconnect',
    CONNECTING: 'connecting',
    USER_CANCELLED: 'user cancelled',
    CONNECT_FAILED: 'connection failed',
    CONNECT_FAILED_IN_CHARGER: 'connection failed in charger',
    CONNECTED: 'connected',
    ROBOT_DATA: 'robot data',
    POWER_STATE: 'robot power state',
    DISCONNECT: 'disconnect robot',
    ROBOT_NAME_UPDATED: 'robot name updated',
    V2_FIRMWARE_OUTDATED: 'firmware outdated',
  },
  DISCOVERY: {
    TOGGLE_CONNECTION: 'toggle connection',
    START: 'begin discovery',
    STOP: 'stop discovery',
    DISCOVERED: 'robot discovered',
  },
  POWER_STATE: {
    LOW: 'low',
    CRITICAL: 'critical',
  },
  FIRMWARE_UPDATE: {
    STARTED: 'firmware update started',
    PROGRESS: 'firmware update progress',
    SUCCEEDED: 'firmware update succeeded',
    FINALIZATION_SUCCEEDED: 'firmware update finalization succeeded',
    FINALIZATION_FAILED: 'firmware update finalization failed',
    FINALIZATION_PROGRESS: 'firmware update finalization progress',
    FAILED: 'firmware update failed',
    BLE_TRANSFER_EXHAUSTED: 'firmware update ble transfer exhausted',
    START_DOWNLOAD: 'start firmware download',
    FINALIZE_UPDATES: 'finalize updates',
  },
  FIRMWARE_STATUS: {
    UPDATE_AVAILABLE: 'firmware update available',
    READY_TO_INSTALL: 'firmware ready to install',
    UP_TO_DATE: 'firmware up to date',
  },
  PROGRAM: {
    SEND_FAILED: 'send program failed',
    SEND_SUCCEEDED: 'send program succeeded',
    SEND_STARTING: 'send program starting',
    STARTING: 'program starting',
    STARTED: 'program executing',
    ERROR: 'program error',
    STOPPED: 'program stopped',
    DOWNLOAD: 'download program',
    DOWNLOAD_RUN: 'download and run program',
    RUN: 'run program',
    STOP: 'stop program',
  },
};

export const ApplicationActions = {
  Drawer: {
    SENSOR_GRAPHS: 'sensor graphs',
    LEFT_DRAWER: 'left drawer',
    RIGHT_DRAWER: 'right drawer',
    CONNECTION_DRAWER: 'connection drawer',
    CONNECTED_DRAWER: 'connected drawer',
    CONTROLLER_DRAWER: 'controller drawer',
    DRIVE_MODE_CONFIG_DRAWER: 'drive mode config drawer',
    DRIVEBASE_DRAWER: 'drivebase drawer',
    SETTINGS_DRAWER: 'settings drawer',
  },
  ConfigurationTabs: {
    MECHANISMS: 'mechanisms',
    DRIVEBASE: 'drivebase',
    CONTROLLER: 'controller',
    DRIVE_MODE: 'drive-mode',
  },
  Tabs: {
    MECHANISMS: 'mechanisms',
    ELECTRONICS: 'electronics',
    ACTIONS: 'actions',
    API: 'api',
    CUSTOM_DATA: 'custom_data',
    DRIVEBASE_DATA: 'drivebase_data',
    MOTOR_DATA: 'motor_data',
    SENSOR_DATA: 'sensor_data',
    OTHER_DATA: 'other_data',
  },
  DashboardViewMode: {
    LIVE: 'live',
    HISTORIC: 'historic',
  },
  Dialogs: {
    CONNECT: { dialog: 'connect dialog', options: { maxWidth: 'sm' } },
    CONNECT_SERIAL: { dialog: 'connect usb dialog', options: { maxWidth: 'sm' } },
    CONNECT_INFO: {
      dialog: 'robot connection info dialog',
      options: { fullWidth: true, maxWidth: 'sm', selectedRobot: '' },
    },
    CONNECTION_PROGRESS: {
      dialog: 'connection progress dialog',
      options: { fullWidth: true, maxWidth: 'sm', disableEscapeKeyDown: true },
    },
    UPLOAD_FILE: { dialog: 'upload file dialog', options: { fullWidth: true, maxWidth: 'md' } },
    LOAD_FROM_SWERVE: { dialog: 'load from swerve dialog', options: { fullWidth: true, maxWidth: 'sm', disableEscapeKeyDown: true } },
    DU_LOGS: { dialog: 'DU logs dialog', options: { fullWidth: true, maxWidth: 'md' } },
    CODE_VALUE_OUT_OF_RANGE: {
      dialog: 'Code value out of range dialog',
      options: { min: undefined, max: undefined, fullWidth: true, maxWidth: 'md' },
    },
    PATH_CREATION: { dialog: 'path creation dialog', options: { maxWidth: 'sm' } },
    FIELD_SELECTION: { dialog: 'field selection dialog', options: { maxWidth: 'lg' } },

    SHARE_CODE_INFO: { dialog: 'share code info dialog', options: { maxWidth: 'md' } },
    OPEN_SHARE_CODE: { dialog: 'open share code dialog', options: { fullWidth: true, maxWidth: 'sm' } },
    EDIT_PIN_SHARE_CODE: { dialog: 'edit pin share code dialog', options: { fullWidth: true, maxWidth: 'sm' } },
    CREATE_SHARE_CODE: { dialog: 'create share code dialog', options: { maxWidth: 'md' } },
    REMOVE_SHARE_CODE: { dialog: 'remove share code dialog', options: { fullWidth: true, maxWidth: 'sm' } },
    REMOVE_COMPLETE_SHARE_CODE: {
      dialog: 'remove complete share code dialog',
      options: { fullWidth: true, maxWidth: 'sm' },
    },

    BPE_ADAPTOR_CHECK: { dialog: 'BPE adaptor check dialog', options: { maxWidth: 'md' } },
    BEAM_BREAK_CHECK: { dialog: 'beam break check dialog', options: { maxWidth: 'md' } },
    DISTANCE_SENSOR_CHECK: { dialog: 'distance sensor check dialog', options: { maxWidth: 'md' } },
    MAGNETIC_SWITCH_CHECK: { dialog: 'magnetic switch check dialog', options: { maxWidth: 'md' } },
    MOTOR_CHECK: { dialog: 'motor check dialog', options: { maxWidth: 'md', disableEscapeKeyDown: true } },

    PROJECT_NAME: { dialog: 'project name dialog', options: { maxWidth: 'md', disableEscapeKeyDown: true } },
    PROJECT_SAVED: { dialog: 'project saved dialog', options: { maxWidth: 'sm' } },
    ROBOT_TEMPLATE: { dialog: 'robot template dialog', options: { maxWidth: 'md' } },
    DELETE_PROJECT: { dialog: 'delete project dialog', options: { maxWidth: 'sm' } },
    SAVE_WARNING_DIALOG: { dialog: 'save warning dialog', options: { maxWidth: 'sm' } },
    OPTIONAL_FIRMWARE_DIALOG: { dialog: 'optional firmware dialog', options: { maxWidth: 'sm' } },
    CRITICAL_FIRMWARE_DIALOG: { dialog: 'critical firmware dialog', options: { maxWidth: 'sm', disableEscapeKeyDown: true } },
    UPDATE_FIRMWARE_CONFIRMATION_DIALOG: { dialog: 'update firmware confirmation dialog', options: { maxWidth: 'sm' } },

    BLUETOOTH_WARNING_DIALOG: { dialog: 'bluetooth warning dialog', options: { maxWidth: 'sm' } },
    FILE_EXPLORER: { dialog: 'file explorer' },
    V2_FIRMWARE_OUTDATED: { dialog: 'firmware unsupported dialog', options: { maxWidth: 'sm' } },
    FIRMWARE_UPDATE_REBOOTING: { dialog: 'firmware update rebooting dialog', options: { maxWidth: 'sm' } },
    FIRMWARE_UPDATING: { dialog: 'firmware updating dialog', options: { maxWidth: 'lg', disableEscapeKeyDown: true } },
    FIRMWARE_STATUS: { dialog: 'firmware status dialog', options: { maxWidth: 'lg' } },
    ABOUT_DIALOG: { dialog: 'about dialog', options: { maxWidth: 'sm' } },
    ACKNOWLEDGMENTS_DIALOG: { dialog: 'acknowledgments dialog', options: { fullWidth: true, maxWidth: 'md' } },
    LOCAL_SAVE_NOTICE: { dialog: 'local save notice dialog', options: { maxWidth: 'sm' } },
    QUOTA_EXCEEDED_ERROR: { dialog: 'quota exceeded error', options: { maxWidth: 'sm' } },
    CANCEL_LESSON_DIALOG: { dialog: 'cancel lesson dialog', options: { maxWidth: 'sm' } },
    RESET_TO_STARTER_PROGRAM_DIALOG: { dialog: 'reset to starter program dialog', options: { maxWidth: 'sm' } },
    LINTER_ERRORS_DIALOG: { dialog: 'linter errors dialog', options: { maxWidth: 'sm' } },
  },
};

// Legacy warning types for backwards compatibility
export const BOLTPLUSWarningTypes = {
  DISPLAY_PROTECTION: {
    CRITICAL: 'protection_status_critical',
    OK: 'protection_status_ok',
    WARN: 'protection_status_warning',
  },
};

// key for locator graph
export const LOC = 'loc';

export const BROWSERS = {
  CHROME: 'Google Chrome',
  FIREFOX: 'Mozilla Firefox',
  EDGE: 'Microsoft Edge',
  IE: 'Internet Explorer',
  SAFARI: 'Apple Safari',
  OPERA: 'Opera',
  SILK: 'Silk',
  UNKNOWN: 'Unknown Browser',
};

export const OPERATING_SYSTEMS = {
  MAC: 'mac',
  WINDOWS: 'windows',
  CHROME: 'chrome',
  UNKNOWN: 'Unknown OS',
};

export const DEFAULT_PROGRAM_CODE = `# Import the Sphero BPS framework
from sphero import *

# Start adding your code here!
`;

export const PATH_BUILDER_SPEED_CONSTRAINTS = {
  min: 0.01,
  max: 1.4,
  step: 0.01,
} as const;

export const waypointConstraints = {
  radius: { minValue: 0, maxValue: 150 },
  rotation: { minValue: 0, maxValue: 360 },
};

/** Detection range options shared by beam break and distance sensors. Label -> threshold in meters. */
export const DETECTION_RANGES = [
  { label: '75mm', valueInMeters: 0.075 },
  { label: '150mm', valueInMeters: 0.15 },
  { label: '200mm', valueInMeters: 0.2 },
  { label: '300mm', valueInMeters: 0.3 },
  { label: '500mm', valueInMeters: 0.5 },
  { label: '3 BME pitch', valueInMeters: 0.075 },
  { label: '6 BME pitch', valueInMeters: 0.15 },
  { label: '8 BME pitch', valueInMeters: 0.2 },
  { label: '12 BME pitch', valueInMeters: 0.3 },
  { label: '20 BME pitch', valueInMeters: 0.5 },
] as const;

export const electronics = {
  highSpeedMotor: {
    ELECTRONIC_TYPE: 'motor_plus_high_speed',
    LOCIZE_TAG: 'high_speed_motor',
    MIN_TORQUE: 0.01,
    MAX_TORQUE: 0.18,
    MAX_RPM: 500,
  },
  highTorqueMotor: {
    ELECTRONIC_TYPE: 'motor_plus_high_torque',
    LOCIZE_TAG: 'high_torque_motor',
    MIN_TORQUE: 0.01,
    MAX_TORQUE: 0.93,
    MAX_RPM: 90,
  },
  BPEadaptor: { ELECTRONIC_TYPE: 'bee_bpr_adapter', LOCIZE_TAG: 'bee_bpr_adapter', MIN_VALUE: -100, MAX_VALUE: 100 },
  beamBreakSensor: {
    ELECTRONIC_TYPE: 'beam_break_sensor',
    LOCIZE_TAG: 'beam_break_sensor',
    DETECTION_RANGES,
  },
  distanceSensor: {
    ELECTRONIC_TYPE: 'distance_sensor',
    LOCIZE_TAG: 'distance_sensor',
    DETECTION_RANGES,
  },
  magneticSwitch: { ELECTRONIC_TYPE: 'magnetic_switch', LOCIZE_TAG: 'magnetic_proximity_sensor' },
};

export const RAPID_ROBOT_BUILD_GROUPS = {
  LaunchParty: 'launch-party',
  BlockParty: 'block-party',
} as const;

export type RapidRobotBuildGroup = (typeof RAPID_ROBOT_BUILD_GROUPS)[keyof typeof RAPID_ROBOT_BUILD_GROUPS];
export type RapidRobotAccent = 'flux' | 'nova' | 'mach' | 'pivot' | 'delta' | 'apex';
export type RapidRobotKey = RapidRobotAccent;

export const MOTOR_TYPE = {
  HIGH_SPEED: 'highSpeedMotor',
  HIGH_TORQUE: 'highTorqueMotor',
} as const;

/** API share code for the default "Python API Only" starter project. */
export const PYTHON_API_TEMPLATE_SHARE_CODE = 'TMP007';

export type BuildElectronic = {
  ELECTRONIC_TYPE: string;
  LOCIZE_TAG: string;
};

export type RobotTemplateDefinition = {
  key: string;
  title?: string;
  electronics: BuildElectronic[] | undefined;
  /** Optional share code for API-backed templates. */
  shareCode?: string;
};

type RapidRobotPresentation = {
  key: RapidRobotKey;
  accent: RapidRobotAccent;
  buildGroup: RapidRobotBuildGroup;
  /** Display order within the Rapid Robots carousel build group. */
  sortInGroup: number;
  nameKey: string;
  name: string;
  subtitleKey: string;
  subtitle: string;
  descriptionKey: string;
  description: string;
  instructionsUrl: string;
  image: string;
};

type SharedBuildDefinition = {
  /** API share code (TMP00x) shared by Rapid Robots cards and robot template projects. */
  shareCode: string;
  /** Robot template dialog list key. */
  templateKey: string;
  title?: string;
  electronics: BuildElectronic[] | undefined;
  rapidRobot?: RapidRobotPresentation;
};

function compareRapidRobotPresentation(a: RapidRobotPresentation, b: RapidRobotPresentation): number {
  if (a.buildGroup !== b.buildGroup) {
    return a.buildGroup === RAPID_ROBOT_BUILD_GROUPS.LaunchParty ? -1 : 1;
  }
  return a.sortInGroup - b.sortInGroup;
}

function toRapidRobotCardFields(presentation: RapidRobotPresentation) {
  const { sortInGroup, ...rapidRobot } = presentation;
  void sortInGroup;
  return rapidRobot;
}

export type RapidRobotDefinition = {
  code: string;
  electronics: BuildElectronic[];
} & Omit<RapidRobotPresentation, 'sortInGroup'>;

/**
 * Single source of truth for API-backed builds: required electronics, template keys, and Rapid Robots UI.
 * `ROBOT_TEMPLATES` and `RAPID_ROBOTS` are derived from this list — update electronics here only.
 */
const SHARED_BUILD_DEFINITIONS: SharedBuildDefinition[] = [
  {
    shareCode: 'TMP001',
    templateKey: 'single_arm_bot',
    electronics: [electronics.highTorqueMotor, electronics.highSpeedMotor],
    rapidRobot: {
      key: 'pivot',
      accent: 'pivot',
      buildGroup: RAPID_ROBOT_BUILD_GROUPS.BlockParty,
      sortInGroup: 0,
      nameKey: 'rapidRobots.pivot.name',
      name: 'PIVOT',
      subtitleKey: 'rapidRobots.pivot.subtitle',
      subtitle: 'Single Arm Robot',
      descriptionKey: 'rapidRobots.pivot.description',
      description: 'Use a single-jointed arm and roller intake to grab blocks quickly and place them with precision.',
      instructionsUrl: 'https://sphero.cc/pivot',
      image: PivotImage,
    },
  },
  {
    shareCode: 'TMP003',
    templateKey: 'dumper_bot',
    electronics: [electronics.highSpeedMotor, electronics.highTorqueMotor],
    rapidRobot: {
      key: 'nova',
      accent: 'nova',
      buildGroup: RAPID_ROBOT_BUILD_GROUPS.LaunchParty,
      sortInGroup: 1,
      nameKey: 'rapidRobots.nova.name',
      name: 'NOVA',
      subtitleKey: 'rapidRobots.nova.subtitle',
      subtitle: 'Cargo Robot',
      descriptionKey: 'rapidRobots.nova.description',
      description: 'Collect balls with a horizontal intake, store them in a hopper, then lift and pour the payload over the wall.',
      instructionsUrl: 'https://sphero.cc/nova',
      image: NovaImage,
    },
  },
  {
    shareCode: 'TMP002',
    templateKey: 'four_bar_arm_bot',
    electronics: [electronics.highTorqueMotor, electronics.highSpeedMotor],
    rapidRobot: {
      key: 'delta',
      accent: 'delta',
      buildGroup: RAPID_ROBOT_BUILD_GROUPS.BlockParty,
      sortInGroup: 1,
      nameKey: 'rapidRobots.delta.name',
      name: 'DELTA',
      subtitleKey: 'rapidRobots.delta.subtitle',
      subtitle: 'Four-Bar Robot',
      descriptionKey: 'rapidRobots.delta.description',
      description: 'Lift blocks smoothly with a four-bar arm that keeps the end effector parallel to the ground.',
      instructionsUrl: 'https://sphero.cc/delta',
      image: DeltaImage,
    },
  },
  {
    shareCode: 'TMP004',
    templateKey: 'conveyor_bot',
    electronics: [electronics.highSpeedMotor],
    rapidRobot: {
      key: 'flux',
      accent: 'flux',
      buildGroup: RAPID_ROBOT_BUILD_GROUPS.LaunchParty,
      sortInGroup: 0,
      nameKey: 'rapidRobots.flux.name',
      name: 'FLUX',
      subtitleKey: 'rapidRobots.flux.subtitle',
      subtitle: 'Conveyor Robot',
      descriptionKey: 'rapidRobots.flux.description',
      description: 'Customize and expand your Flux build with a conveyor system that moves game pieces efficiently.',
      instructionsUrl: 'https://sphero.cc/flux',
      image: FluxImage,
    },
  },
  {
    shareCode: 'TMP006',
    templateKey: 'elevator_bot',
    electronics: [electronics.highSpeedMotor, electronics.highTorqueMotor],
    rapidRobot: {
      key: 'apex',
      accent: 'apex',
      buildGroup: RAPID_ROBOT_BUILD_GROUPS.BlockParty,
      sortInGroup: 2,
      nameKey: 'rapidRobots.apex.name',
      name: 'APEX',
      subtitleKey: 'rapidRobots.apex.subtitle',
      subtitle: 'Elevator Robot',
      descriptionKey: 'rapidRobots.apex.description',
      description: 'Raise blocks with a chain-driven elevator and release them from a geared claw when it is time to score.',
      instructionsUrl: 'https://sphero.cc/apex',
      image: ApexImage,
    },
  },
  {
    shareCode: 'TMP005',
    templateKey: 'launcher_bot',
    electronics: [electronics.highSpeedMotor, electronics.highSpeedMotor, electronics.highTorqueMotor],
    rapidRobot: {
      key: 'mach',
      accent: 'mach',
      buildGroup: RAPID_ROBOT_BUILD_GROUPS.LaunchParty,
      sortInGroup: 2,
      nameKey: 'rapidRobots.mach.name',
      name: 'MACH',
      subtitleKey: 'rapidRobots.mach.subtitle',
      subtitle: 'Launcher Robot',
      descriptionKey: 'rapidRobots.mach.description',
      description: 'Launch balls over the barrier with a flywheel that compresses them against a curved launcher hood.',
      instructionsUrl: 'https://sphero.cc/mach',
      image: MachImage,
    },
  },
  {
    shareCode: 'TMP008',
    templateKey: 'custom_bot',
    electronics: undefined,
  },
];

export const ROBOT_TEMPLATES: RobotTemplateDefinition[] = SHARED_BUILD_DEFINITIONS.map((build) => ({
  key: build.templateKey,
  shareCode: build.shareCode,
  electronics: build.electronics,
  title: build.title,
}));

export const RAPID_ROBOTS = SHARED_BUILD_DEFINITIONS.filter(
  (build): build is SharedBuildDefinition & { rapidRobot: RapidRobotPresentation } => build.rapidRobot != null
)
  .sort((a, b) => compareRapidRobotPresentation(a.rapidRobot, b.rapidRobot))
  .map((build) => ({
    code: build.shareCode,
    electronics: build.electronics ?? [],
    ...toRapidRobotCardFields(build.rapidRobot),
  }));

export const RAPID_ROBOT_COMPETITIONS = [
  {
    key: RAPID_ROBOT_BUILD_GROUPS.LaunchParty,
    titleKey: 'rapidRobots.carousel.buildGroup.launchParty',
    title: 'Launch Party Competition',
    descriptionKey: 'rapidRobots.competition.launchParty.description',
    description:
      "Use your Rapid Robot to collect and launch as many balls over the Dividing Wall into your opponent's zone before running out of time. The team with the most balls on the other side wins!",
    gameRulesUrl: 'https://sphero.cc/launchparty',
    robotKeys: ['flux', 'nova', 'mach'] satisfies RapidRobotKey[],
  },
  {
    key: RAPID_ROBOT_BUILD_GROUPS.BlockParty,
    titleKey: 'rapidRobots.carousel.buildGroup.blockParty',
    title: 'Block Party Competition',
    descriptionKey: 'rapidRobots.competition.blockParty.description',
    description: 'Use your Rapid Robot to collect blocks and place them strategically in the Scoring Tower. The team with the most points wins!',
    gameRulesUrl: 'https://sphero.cc/blockparty',
    robotKeys: ['pivot', 'delta', 'apex'] satisfies RapidRobotKey[],
  },
] as const;

export const EXAMPLE_TAGS = ['All', 'Code', 'Motor', 'Competition', 'Robot'];

export const RUNMODE = {
  AUTONOMOUS: 'RunMode.AUTONOMOUS',
  MATCH: 'RunMode.MATCH',
  DRIVER: 'RunMode.DRIVER',
} as const;

export const DRIVE_MODE = {
  DRIVE_CONTROL: 'drive-control',
  AUTONOMOUS: 'autonomous',
  MATCH: 'match',
} as const;

export type DriveMode = (typeof DRIVE_MODE)[keyof typeof DRIVE_MODE];

export const START_CALL_TOKEN = '.start(';

export const ARG_KEYS = {
  MODE: 'mode',
  UPDATE_PERIOD: 'update_period',
  AUTO_MATCH_TIME: 'auto_match_time',
  DRIVER_MATCH_TIME: 'driver_match_time',
} as const;

export const ROBOT_PROP = {
  AUTONOMOUS_ACTION: 'autonomous_action',
} as const;

export const MATCH_TIME_DEFAULTS = {
  AUTO: '15.0',
  DRIVER: '90.0',
} as const;
